import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  Search, 
  Phone, 
  Video, 
  MoreVertical, 
  Paperclip, 
  Smile,
  X,
  ArrowLeft,
  Online,
  CheckCheck,
  Check,
  Clock,
  Star,
  User,
  Image,
  File
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Mock chat data - in real app this would come from Supabase
const mockChats = [
  {
    id: 1,
    participants: [
      { id: 'user1', name: 'Sarah van Dijk', avatar: 'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=100', online: true },
      { id: 'user2', name: 'Angela Sissoko', avatar: 'https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&w=100', online: true }
    ],
    lastMessage: 'Hoi! Ik ben geïnteresseerd in je marketing coaching sessie.',
    timestamp: '2 min geleden',
    unread: 2,
    service: 'Marketing Strategy Consulting'
  },
  {
    id: 2,
    participants: [
      { id: 'user3', name: 'Luna Fotografie', avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=100', online: false },
      { id: 'user2', name: 'Angela Sissoko', avatar: 'https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&w=100', online: true }
    ],
    lastMessage: 'Perfect! Ik kan volgende week dinsdag om 14:00',
    timestamp: '1 uur geleden',
    unread: 0,
    service: 'Professional Photography'
  },
  {
    id: 3,
    participants: [
      { id: 'user4', name: 'Maya Tech Solutions', avatar: 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=100', online: true },
      { id: 'user2', name: 'Angela Sissoko', avatar: 'https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&w=100', online: true }
    ],
    lastMessage: 'Ik stuur je straks de eerste designs door',
    timestamp: '3 uur geleden',
    unread: 1,
    service: 'Website Development'
  }
];

const mockMessages = [
  {
    id: 1,
    chatId: 1,
    senderId: 'user1',
    message: 'Hoi Angela! Ik ben geïnteresseerd in je marketing coaching sessie.',
    timestamp: '14:30',
    status: 'read',
    type: 'text'
  },
  {
    id: 2,
    chatId: 1,
    senderId: 'user2',
    message: 'Hoi Sarah! Leuk dat je interesse hebt. Waar kan ik je mee helpen?',
    timestamp: '14:32',
    status: 'read',
    type: 'text'
  },
  {
    id: 3,
    chatId: 1,
    senderId: 'user1',
    message: 'Ik wil graag mijn online aanwezigheid versterken. Kun je me helpen met een strategie?',
    timestamp: '14:33',
    status: 'read',
    type: 'text'
  },
  {
    id: 4,
    chatId: 1,
    senderId: 'user2',
    message: 'Absoluut! Ik kan je helpen met een complete digital marketing strategie. Zullen we een call plannen?',
    timestamp: '14:35',
    status: 'delivered',
    type: 'text'
  },
  {
    id: 5,
    chatId: 1,
    senderId: 'user1',
    message: 'Dat zou geweldig zijn! Wanneer heb je tijd?',
    timestamp: '14:36',
    status: 'sent',
    type: 'text'
  }
];

interface ChatSystemProps {
  isOpen: boolean;
  onClose: () => void;
  initialChatId?: number;
}

const ChatSystem: React.FC<ChatSystemProps> = ({ isOpen, onClose, initialChatId }) => {
  const { user } = useAuth();
  const [selectedChat, setSelectedChat] = useState<number | null>(initialChatId || null);
  const [messages, setMessages] = useState(mockMessages);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (initialChatId) {
      setSelectedChat(initialChatId);
    }
  }, [initialChatId]);

  // Real-time chat subscription (would be implemented with Supabase)
  useEffect(() => {
    if (!isOpen) return;

    // Mock real-time functionality
    const interval = setInterval(() => {
      // Simulate typing indicator
      if (Math.random() < 0.1) {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;

    const newMsg = {
      id: messages.length + 1,
      chatId: selectedChat,
      senderId: 'user2', // Current user
      message: newMessage,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: 'sent' as const,
      type: 'text' as const
    };

    setMessages(prev => [...prev, newMsg]);
    setNewMessage('');

    // Simulate message delivery
    setTimeout(() => {
      setMessages(prev => prev.map(msg => 
        msg.id === newMsg.id ? { ...msg, status: 'delivered' } : msg
      ));
    }, 1000);

    // Simulate message read
    setTimeout(() => {
      setMessages(prev => prev.map(msg => 
        msg.id === newMsg.id ? { ...msg, status: 'read' } : msg
      ));
    }, 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredChats = mockChats.filter(chat => 
    chat.participants.some(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || chat.service.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedChatData = mockChats.find(chat => chat.id === selectedChat);
  const currentChatMessages = messages.filter(msg => msg.chatId === selectedChat);

  const getOtherParticipant = (chat: typeof mockChats[0]) => {
    return chat.participants.find(p => p.id !== 'user2'); // Current user is user2
  };

  const getMessageStatus = (status: string) => {
    switch (status) {
      case 'sent':
        return <Check className="h-3 w-3 text-navy-400" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-navy-400" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      default:
        return <Clock className="h-3 w-3 text-navy-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[80vh] flex overflow-hidden">
        {/* Chat List */}
        <div className={`w-full md:w-1/3 border-r border-cream-300 flex flex-col ${selectedChat ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-cream-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-navy-800">Berichten</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="hover:bg-cream-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-navy-400" />
              <Input
                placeholder="Zoek gesprekken..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-cream-50 border-cream-300"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredChats.map((chat) => {
              const otherParticipant = getOtherParticipant(chat);
              return (
                <div
                  key={chat.id}
                  className={`p-4 hover:bg-cream-50 cursor-pointer transition-colors border-b border-cream-200 ${
                    selectedChat === chat.id ? 'bg-cream-100' : ''
                  }`}
                  onClick={() => setSelectedChat(chat.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={otherParticipant?.avatar} />
                        <AvatarFallback>
                          {otherParticipant?.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      {otherParticipant?.online && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-navy-800 truncate">
                          {otherParticipant?.name}
                        </h4>
                        <span className="text-xs text-navy-500">{chat.timestamp}</span>
                      </div>
                      <p className="text-sm text-navy-600 truncate">{chat.lastMessage}</p>
                      <Badge variant="outline" className="text-xs mt-1 border-cream-400">
                        {chat.service}
                      </Badge>
                    </div>
                    {chat.unread > 0 && (
                      <Badge className="bg-navy-700 text-white text-xs min-w-5 h-5 flex items-center justify-center">
                        {chat.unread}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat Messages */}
        {selectedChat ? (
          <div className="flex-1 flex flex-col">
            {/* Chat Header */}
            <div className="p-4 border-b border-cream-300 bg-cream-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedChat(null)}
                    className="md:hidden hover:bg-cream-100"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="relative">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getOtherParticipant(selectedChatData!)?.avatar} />
                      <AvatarFallback>
                        {getOtherParticipant(selectedChatData!)?.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    {getOtherParticipant(selectedChatData!)?.online && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-800">
                      {getOtherParticipant(selectedChatData!)?.name}
                    </h4>
                    <p className="text-xs text-navy-500">
                      {getOtherParticipant(selectedChatData!)?.online ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="hover:bg-cream-100">
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="hover:bg-cream-100">
                    <Video className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="hover:bg-cream-100">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Badge variant="outline" className="text-xs mt-2 border-cream-400">
                Over: {selectedChatData?.service}
              </Badge>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {currentChatMessages.map((message) => {
                const isOwnMessage = message.senderId === 'user2';
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isOwnMessage
                          ? 'bg-navy-700 text-white'
                          : 'bg-cream-200 text-navy-800'
                      }`}
                    >
                      <p className="text-sm">{message.message}</p>
                      <div className={`flex items-center gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <span className={`text-xs ${isOwnMessage ? 'text-navy-200' : 'text-navy-500'}`}>
                          {message.timestamp}
                        </span>
                        {isOwnMessage && getMessageStatus(message.status)}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-cream-200 text-navy-800 px-4 py-2 rounded-lg">
                    <div className="flex items-center gap-1">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-navy-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-navy-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-navy-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-xs text-navy-500 ml-2">Typen...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-cream-300 bg-cream-50">
              <div className="flex items-end gap-2">
                <Button variant="ghost" size="sm" className="hover:bg-cream-100">
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="hover:bg-cream-100">
                  <Image className="h-4 w-4" />
                </Button>
                <div className="flex-1">
                  <Textarea
                    placeholder="Type een bericht..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="min-h-[40px] max-h-32 resize-none bg-white border-cream-300"
                    rows={1}
                  />
                </div>
                <Button 
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-navy-700 hover:bg-navy-800 text-white"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-cream-50">
            <div className="text-center">
              <MessageCircle className="h-16 w-16 mx-auto mb-4 text-navy-300" />
              <h3 className="text-lg font-medium text-navy-700 mb-2">Selecteer een gesprek</h3>
              <p className="text-navy-500">Kies een gesprek uit de lijst om berichten te bekijken</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSystem;